import React, { useState, useMemo } from 'react';
import { bcGovernmentServices } from '../../data/bcGovernmentServices';
import { services } from '../../data/services';

interface SearchPanelProps {
  onClose: () => void;
  primaryColor: string;
}

const SearchPanel: React.FC<SearchPanelProps> = ({ onClose, primaryColor }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const allServices = useMemo(() => {
    const bcServices = bcGovernmentServices.map(s => ({
      id: s.id, name: s.name, description: s.description,
      category: s.category, link: s.link, phone: s.phone,
      priority: s.priority, source: 'BC Government',
    }));
    const mainServices = Object.values(services).map(s => ({
      id: s.id, name: s.name, description: s.description,
      category: 'Newcomer Services', link: s.link, phone: undefined,
      priority: 'medium' as const, source: 'Partner Organizations',
    }));
    return [...bcServices, ...mainServices];
  }, []);

  const filteredServices = useMemo(() => {
    if (!searchQuery.trim()) return allServices;
    const query = searchQuery.toLowerCase();
    return allServices.filter(s =>
      s.name.toLowerCase().includes(query) ||
      s.description.toLowerCase().includes(query) ||
      s.category.toLowerCase().includes(query)
    );
  }, [searchQuery, allServices]);

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-start z-[1000] p-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl mt-8" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 rounded-t-lg" style={{ backgroundColor: primaryColor }}>
          <h2 className="text-white text-xl font-bold m-0">Search All Services</h2>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer p-1 flex items-center rounded hover:bg-white/20 transition-colors">
            <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative mb-3">
            <svg width="20" height="20" fill="#6B7280" viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2">
              <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
            </svg>
            <input
              type="text"
              placeholder="Search by service name, description, or category..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
              className="w-full pl-10 pr-10 py-3 text-base border-2 border-gray-200 rounded-lg outline-none focus:border-blue-500"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-red-500 text-white border-none w-6 h-6 rounded-full cursor-pointer flex items-center justify-center text-sm">
                ✕
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500">
            {searchQuery
              ? <>Found <strong>{filteredServices.length}</strong> services</>
              : <>Showing all <strong>{allServices.length}</strong> services</>}
          </p>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredServices.length > 0 ? (
            filteredServices.map(service => (
              <div key={service.id} className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-gray-900 m-0">{service.name}</h3>
                  <span className="text-xs text-gray-500 bg-gray-200 px-3 py-1 rounded-full font-medium">{service.source}</span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm text-gray-800 bg-gray-200 px-3 py-1 rounded font-medium">{service.category}</span>
                  {service.priority === 'high' && (
                    <span className="text-xs text-yellow-800 bg-yellow-300 px-3 py-1 rounded-full font-bold">High Priority</span>
                  )}
                  <div className="ml-auto flex gap-2">
                    {service.phone && (
                      <a href={`tel:${service.phone}`}
                        className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded no-underline hover:bg-emerald-600 transition-colors">
                        📞 Call
                      </a>
                    )}
                    <a href={service.link} target="_blank" rel="noopener noreferrer"
                      className="px-4 py-2 text-white text-sm font-medium rounded no-underline hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: primaryColor }}>
                      🔗 Visit Website
                    </a>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 text-gray-400">
              <svg width="64" height="64" fill="#D1D5DB" viewBox="0 0 24 24" className="mx-auto mb-4">
                <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
              </svg>
              <h3 className="text-xl text-gray-600 mb-2">No services found</h3>
              <p className="text-base m-0">Try different keywords or browse categories in the sidebar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPanel;
